<template>
<div class="map"
     :class="{ 'map--fullscreen' : isFullscreen ,'map--error': hasKinks}">
  <div v-if="sidebarShown" class="map__sidebar">
    <slot name="fullscreen-sidebar" />
  </div>
  <div class="map__container"
       :id="mapId"
       :style="{
                height:height,
                width: width,
            }" />
  <div v-if="buttonsPressent" class="map__buttons">
    <LoadingIndicator v-if="isLoading" />
    <slot />
    <MapButton v-if="canDraw"
               icon="layers_clear"
               @click="clearDrawing"
               tooltip="Wis invoer"
    />

    <MapButton v-if="layers.length > 1"
               tooltip="Kaart achtergrond"
               icon="layers"
    >
      <q-menu auto-close anchor="top right" self="bottom right">
        <q-list style="min-width: 100px">
          <template v-if="backgroundLayers.length">
            <q-item v-for="layer in backgroundLayers" clickable v-ripple @click="enableLayer(layer)">
              <q-item-section v-if="layer.isEnabled"><strong>{{ layer.name }}</strong></q-item-section>
              <q-item-section v-else>{{ layer.name }}</q-item-section>
            </q-item>
          </template>
          <q-separator v-if="backgroundLayers.length && otherLayers.length" />
          <template v-if="otherLayers.length">
            <q-item v-for="layer in otherLayers" clickable v-ripple @click="enableLayer(layer)">
              <q-item-section v-if="layer.isEnabled"><strong>{{ layer.name }}</strong></q-item-section>
              <q-item-section v-else>{{ layer.name }}</q-item-section>
            </q-item>
          </template>
        </q-list>
      </q-menu>
    </MapButton>

    <MapButton v-if="canFullscreen"
               :icon="isFullscreen ? 'fullscreen_exit' : 'fullscreen'"
               @click="toggleFullscreen(!isFullscreen)"
               :tooltip="isFullscreen ? 'Sluit volledige weergave' : 'Volledige weergave' "
    />

    <q-btn-group v-if="canZoom">
      <MapButton :disabled="zoomMaxed"
                 @click="zoomIn"
                 icon="add"
                 tooltip="Zoom in" />
      <MapButton :disabled="zoomMinned"
                 @click="zoomOut"
                 dense
                 icon="remove"
                 tooltip="Zoom uit" />
    </q-btn-group>
  </div>
  <div v-if="isFullscreen"
       class="map__backdrop"
       @click="toggleFullscreen(false)" />
</div>
</template>

<script>
import '~HIOR/classes/map/layers'
import 'leaflet-draw'
import 'proj4leaflet'
import 'proj4'
import turf from 'turf'
import _ from 'lodash'
import L from 'leaflet'
import {
  BOUND_NORTH,
  BOUND_EAST,
  BOUND_SOUTH,
  BOUND_WEST,
  CENTER_LAT,
  CENTER_LNG,
  ZOOM,
} from '~HIOR/constants/mapBounds'
import 'leaflet/dist/leaflet.css'
import { LayerManager } from '~HIOR/classes/map'
import MapButton from '../button/MapButton'

export default {
  components: { MapButton },
  emits: ['feature:draw', 'feature:hover', 'feature:click', 'update:geometry'],
  props: {
    layers: {
      type: Array,
      default: () => LayerManager.getLayers(),
    },
    height: {
      type: String,
      default: '200px',
    },
    width: {
      type: String,
      default: '100%',
    },
    geometry: {
      type: [String, Object],
      default: null,
    },
    autoFit: {
      type: Boolean,
      default: false,
    },
    autoFitAlways: {
      type: Boolean,
      default: false,
    },
    canFullscreen: {
      type: Boolean,
      default: true,
    },
    canZoom: {
      type: Boolean,
      default: true,
    },
    canPan: {
      type: Boolean,
      default: true,
    },
    canDraw: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    map: null,
    geometryLayer: null,
    zoomMaxed: false,
    zoomMinned: false,
    isFullscreen: false,
    hasKinks: false,
    drawMode: null,
    editMode: null,
    featurePaths: [],
  }),
  watch: {
    geometry () {
      if (this.hasKinks) return
      this.setGeometry()
    },
  },
  computed: {
    mapId () {
      return `map_${document.querySelectorAll('.map__container').length + 1}`
    },
    backgroundLayers () {
      return this.layers.filter(layer => layer.isBackgroundLayer)
    },
    otherLayers () {
      return this.layers.filter(layer => !layer.isBackgroundLayer)
    },
    buttonsPressent () {
      return this.map && (this.layers.length || this.canFullscreen || this.canZoom)
    },
    sidebarShown () {
      return this.isFullscreen && this.$slots['fullscreen-sidebar']
    },
  },
  methods: {
    async setupMap () {
      const map = new L.Map(this.mapId, {
        continuousWorld: true,
        crs: new L.Proj.CRS('EPSG:28992', '+proj=sterea +lat_0=52.15616055555555 +lon_0=5.38763888888889 +k=0.9999079 +x_0=155000 +y_0=463000 +ellps=bessel +units=m +towgs84=565.2369,50.0087,465.658,-0.406857330322398,0.350732676542563,-1.8703473836068,4.0812 +no_defs', { // eslint-disable-line no-undef
          transformation: L.Transformation(-1, -1, 0, 0),
          resolutions: [3440.640, 1720.320, 860.160, 430.080, 215.040, 107.520, 53.760, 26.880, 13.440, 6.720, 3.360, 1.680, 0.840, 0.420],
          origin: [-285401.920, 903401.920],
          bounds: L.bounds([-285401.920, 903401.920], [595401.920, 22598.080]),
        }),
        zoom: ZOOM,
        zoomControl: false,
        scrollWheelZoom: this.canZoom,
        touchZoom: this.canZoom,
        doubleClickZoom: this.canZoom,
        dragging: this.canPan,
        center: new L.LatLng(CENTER_LAT, CENTER_LNG),
        maxBounds: L.latLngBounds(
            L.latLng(BOUND_NORTH, BOUND_EAST),
            L.latLng(BOUND_SOUTH, BOUND_WEST),
        ),
      })

      if (this.canDraw) {
        this.setupDrawAndEditMode(map)
      } else {
        this.setupDisplayMode(map)
      }

      this.layers.forEach(layer => layer.addToMap(map))
      if (this.backgroundLayers.length) {
        this.enableLayer(this.backgroundLayers[0])
      } else {
        const background = new LayerManager.getDefault()
        background.addToMap(map)
        this.enableLayer(background)
      }
      this.map = map
      await this.setGeometry(true)
      this.checkZoomBounds()
      map.on('zoomend', this.checkZoomBounds)
      map.attributionControl.setPrefix(
          '<a href="https://github.com/Leaflet/Leaflet/tree/v1.7.1" title="A JavaScript library for interactive maps">Leaflet</a>',
      )
      return this.redraw()
    },
    setupDisplayMode (map) {
      this.geometryLayer = new L.GeoJSON(null, {
        style: {
          className: 'geometry__path',
        },
        onEachFeature: (feature, polygon) => {
          this.featurePaths.push({ properties: feature.properties, polygon })
          this.$nextTick(() => this.$emit('feature:draw', { properties: feature.properties, polygon }))
          polygon.on('click', () => this.$emit('feature:click', feature.properties))
          polygon.on('mouseover', () => this.$emit('feature:hover', feature.properties))
        },
      })
      this.geometryLayer.addTo(map)
    },
    setupDrawAndEditMode (map) {
      this.geometryLayer = new L.GeoJSON(null, {
        style: {
          className: 'geometry__path geometry__path--drawable',
        },
        onEachFeature: (feature, polygon) => {
          polygon.on('click', () => this.editMode.enable())
        },
      })
      this.geometryLayer.addTo(map)

      this.drawMode = new L.Draw.Polygon(map, {
        shapeOptions: {
          className: 'geometry__path geometry__path--drawing',
        },
      })

      this.editMode = new L.EditToolbar.Edit(map, {
        featureGroup: this.geometryLayer,
        selectedPathOptions: {
          className: 'geometry__path geometry__path--drawing',
        },
      })

      L.drawLocal.draw.handlers.polygon.tooltip = {
        start: ' ',
        cont: ' ',
        end: ' ',
      }
      L.drawLocal.edit.handlers.edit.tooltip = {
        subtext: ' ',
        text: ' ',
      }

      map.on('draw:created', (e) => {
        this.drawMode.disable()
        this.geometryLayer.addLayer(e.layer)
        this.updateGeometry()
        e.layer.on('click', () => {
          this.editMode.enable()
        })
      })
      map.on('draw:editvertex', () => {
        this.updateGeometry()
      })
      map.on('zoomstart', () => {
        this.editMode.disable()
      })
      map.on('movestart', () => {
        this.editMode.disable()
      })
      this.drawMode.enable()
      this.editMode.disable()
    },
    async setGeometry (initial = false) {
      this.geometryLayer.clearLayers()
      this.featurePaths = []
      if (!this.geometry) return
      let polygon = null
      let geometry = null

      if (typeof this.geometry === 'string') {
        geometry = JSON.parse(this.geometry)
      } else {
        geometry = this.geometry
      }
      const polygons = []
      if (geometry.type === 'MultiPolygon' && this.canDraw) {
        geometry = geometry.coordinates[0]
      }
      if (_.isArray(geometry)) {
        if (_.isArray(geometry[0][0])) {
          polygon = L.polygon(geometry.map((polygon) => polygon.map(pair => [pair[1], pair[0]])))
        } else {
          polygon = L.polygon(geometry.map((pair) => [pair[1], pair[0]]))
        }
        this.geometryLayer.addData(polygon.toGeoJSON())
      } else {
        this.geometryLayer.addData(geometry)
      }
      if ((initial && this.autoFit) || this.autoFitAlways) {
        await this.$nextTick()
        this.map.fitBounds(this.geometryLayer.getBounds())
      }

      if (!initial) {
        return
      }

      if (this.canDraw) {
        this.drawMode.disable()
        this.editMode.enable()
        this.updateGeometry()
      }
    },
    enableLayer (layer) {
      if (layer.isBackgroundLayer) {
        this.backgroundLayers.forEach(otherLayer => {
          otherLayer.disable()
        })
      }
      layer.enable()
      this.geometryLayer.bringToFront()
    },
    clearDrawing () {
      this.geometryLayer.clearLayers()
      this.drawMode.enable()
      this.editMode.disable()
      this.$emit('update:geometry', '')
    },
    zoomIn () {
      this.map.zoomIn()
    },
    zoomOut () {
      this.map.zoomOut()
    },
    checkZoomBounds () {
      const zoom = this.map.getZoom()
      this.zoomMaxed = this.map.getMaxZoom() === zoom
      this.zoomMinned = this.map.getMinZoom() === zoom
    },
    async toggleFullscreen (isFullscreen) {
      this.isFullscreen = isFullscreen
      this.redraw()
      if (this.autoFit || this.autoFitAlways) {
        await this.$nextTick()
        this.map.fitBounds(this.geometryLayer.getBounds())
      }
    },
    async redraw () {
      await this.$nextTick()
      this.map.invalidateSize()
    },
    highlightFeature (identifier, centerMap = false, key = 'uuid') {
      this.removeHighlights()
      const feature = this.featurePaths.find(feature => feature.properties[key] === identifier)
      if (!feature?.polygon?._path) return
      feature.polygon._path.classList.add('geometry__path--highlight')
      if (!centerMap) return
      const center = feature.polygon.getBounds().getCenter()
      if (this.map.getBounds().contains(center)) return
      this.map.panTo(center, {
        duration: 0.5,
        animate: true,
      })
    },
    updateGeometry () {
      if (_.isEmpty(this.geometryLayer._layers)) return
      this.hasKinks = Object.values(this.geometryLayer._layers).some((layer) => {
        return turf.kinks(layer.toGeoJSON()).features.length > 0
      })
      if (this.hasKinks) {
        this.editMode.enable()
        return this.$emit('update:geometry', '')
      }
      this.$emit('update:geometry', JSON.stringify(this.geometryLayer.toGeoJSON().features[0].geometry.coordinates[0]))
    },
    removeHighlights () {
      Array.from(this.$el.querySelectorAll('.geometry__path--highlight'))
          .forEach(element => {
            element.classList.remove('geometry__path--highlight')
          })
    },
  },
  mounted () {
    this.setupMap()
  },
}
</script>
